import { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationService, Notification } from '@/services/notification.service';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatTimestamp } from '@/utils/dateFormat';
import { translateNotificationText } from '@/utils/notificationI18n';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

export default function NotificationPanel({ open, onClose, onUnreadCountChange }: NotificationPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 20;

  // 加载通知列表
  const loadNotifications = async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications({
        page: pageNum,
        page_size: pageSize,
      });

      if (response) {
        const newNotifications = (response as { list?: Notification[] }).list ?? (response as { data?: { list?: Notification[] } }).data?.list ?? [];
        if (append) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }
        setTotal(response.total || 0);
        setHasMore(newNotifications.length === pageSize);

        // 更新未读数量
        const unreadCount = newNotifications.filter((n: Notification) => n.is_read === 0).length;
        if (onUnreadCountChange && !append) {
          onUnreadCountChange(unreadCount);
        }
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      toast.error(t('notifications.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 打开面板时加载通知
  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  // 加载更多
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadNotifications(nextPage, true);
  };

  // 标记单个通知为已读并跳转
  const handleNotificationClick = async (notification: Notification) => {
    try {
      // 如果未读，标记为已读
      if (notification.is_read === 0) {
        await notificationService.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: 1 } : n
          )
        );
        // 更新未读数量
        if (onUnreadCountChange) {
          const unreadCount = notifications.filter(n => n.id !== notification.id && n.is_read === 0).length;
          onUnreadCountChange(unreadCount);
        }
      }

      // 如果有链接，跳转
      if (notification.link_url) {
        onClose();

        // 转换旧的URL格式为新格式（兼容数据库中的旧通知）
        let targetUrl = notification.link_url;

        // 将 /tickets/admin/:id 转换为 /tickets/chat/customer/:id
        if (targetUrl.match(/^\/tickets\/admin\/[^/]+$/)) {
          const ticketId = targetUrl.split('/').pop();
          targetUrl = `/tickets/chat/customer/${ticketId}`;
        }

        // 将 /tickets/admin/supplier/:id 转换为 /tickets/chat/supplier/:id
        if (targetUrl.match(/^\/tickets\/admin\/supplier\/[^/]+$/)) {
          const ticketId = targetUrl.split('/').pop();
          targetUrl = `/tickets/chat/supplier/${ticketId}`;
        }

        navigate(targetUrl);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // 标记所有为已读
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      if (onUnreadCountChange) {
        onUnreadCountChange(0);
      }
      toast.success(t('notifications.markedAllAsRead'));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error(t('notifications.markAllFailed'));
    }
  };

  // 删除通知
  const handleDelete = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await notificationService.deleteNotification(id);
      const deletedNotification = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setTotal(prev => prev - 1);

      // 如果删除的是未读通知，更新未读数量
      if (deletedNotification && deletedNotification.is_read === 0 && onUnreadCountChange) {
        const unreadCount = notifications.filter(n => n.id !== id && n.is_read === 0).length;
        onUnreadCountChange(unreadCount);
      }

      toast.success(t('notifications.deleted'));
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error(t('notifications.deleteFailed'));
    }
  };

  const unreadCount = notifications.filter(n => n.is_read === 0).length;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {t('notifications.title')}
            </Typography>
            {unreadCount > 0 && (
              <Badge badgeContent={unreadCount} color="error" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadCount > 0 && (
              <IconButton size="small" onClick={handleMarkAllAsRead} title={t('notifications.markAllAsRead')}>
                <DoneAllIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">{t('notifications.empty')}</Alert>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.is_read === 0 ? 'action.hover' : 'transparent',
                      '&:hover': {
                        bgcolor: notification.is_read === 0 ? 'action.selected' : 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {notification.is_read === 0 && (
                            <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                          )}
                          <Typography variant="body2" fontWeight={notification.is_read === 0 ? 600 : 400}>
                            {translateNotificationText(notification.title, t)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {translateNotificationText(notification.content, t)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatTimestamp(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(notification.id, e)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                  {index < notifications.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}

          {/* Load More Button */}
          {hasMore && notifications.length > 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button onClick={handleLoadMore} disabled={loading} size="small">
                {loading ? <CircularProgress size={20} /> : t('common.loadMore')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Footer */}
        {total > 0 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('notifications.total', { count: total })}
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
